// Step-by-step attempt / service entry ("tap an answer, go to the next screen").
// CommonJS, no destructuring (a Babel helper import would turn this file into an
// ES module in the production build and break named imports). Pure: no DOM.
//
// The wizard keeps its own answers on the note form (keys starting with "wz")
// and, after every answer, re-derives the EXISTING form fields the save path,
// the persistence mapper and the proof generator already read (outcome, status,
// serviceType, personPresent, personSaid, observed, otherContact, …). Nothing
// downstream changes: an attempt saved from the wizard is the same record the
// old form produced.

const OTHER = "Other";

// ── Option lists ─────────────────────────────────────────────────────────────
const WHO_SPOKE = [
  { value: "nobody", label: "Nobody" },
  { value: "spouse", label: "Spouse" },
  { value: "parent", label: "Parent" },
  { value: "son", label: "Son" },
  { value: "daughter", label: "Daughter" },
  { value: "sibling", label: "Sibling" },
  { value: "roommate", label: "Roommate" },
  { value: "resident", label: "Resident" },
  { value: "other", label: OTHER, text: "Who did you speak with?" },
];
const WHAT_SAID = [
  "Subject resides but not available",
  "Subject resides but refused to respond",
  "Subject unknown",
  "Subject moved",
  "Subject no longer employed",
  "Service not permitted",
  "Subject deceased",
  "Subject employed but not available",
  "Subject receives mail but not available",
  "Subject incarcerated",
  "Subject known but never resided",
  "Subject resides in a care facility",
].map((l) => ({ value: l, label: l })).concat([{ value: OTHER, label: OTHER, text: "What did they say?" }]);
const OBSERVED = [
  "Nothing",
  "Package/mail addressed to subject",
  "Call/mail box listing subject",
  "Previously served subject",
  "Package/mail addressed to another",
  "Call/mail box listing another",
].map((l) => ({ value: l, label: l }));
const OTHER_CONTACT = [
  "Nobody",
  "Neighbor who says subject resides",
  "Neighbor who says not resident",
  "Neighbor who doesn't recognize name",
  "Property manager/landlord who says subject resides",
  "Property manager/landlord who says not resident",
  "Property manager/landlord who doesn't recognize name",
  "On-site worker who says subject resides",
  "On-site worker who says not resident",
  "On-site worker who doesn't recognize name",
].map((l) => ({ value: l, label: l }));
const WHAT_HAPPENED = [
  { value: "no_answer", label: "No answer at door" },
  { value: "no_access", label: "No access" },
  { value: "vacant", label: "Vacant property" },
  { value: "need_unit", label: "Need unit number" },
  { value: "no_such_address", label: "No such address" },
  { value: "pmb", label: "PMB/PO Box" },
  { value: "hostile", label: "Hostile environment" },
  { value: "other", label: OTHER, text: "What happened?" },
];
const NO_ACCESS = [
  "Secured building", "Gated community", "Business closed", "Private gate/fence",
  "Unrestrained animal", "Hostile environment", "Impassable road", "Flood", "Fire", "Storm",
].map((l) => ({ value: l, label: l })).concat([{ value: OTHER, label: OTHER, text: "Why no access?" }]);
const ADDRESS_TYPES = ["Business", "Residence", "Mail Facility", "Correctional Facility", "Other"]
  .map((l) => ({ value: l, label: l }));
const IDENTITY = [
  "Subject stating their name",
  "Subject saying yes when named",
  "Subject nodding when named",
  "Subject reaching for docs when named",
  "Subject showing identification",
  "Neighbor",
].map((l) => ({ value: l, label: l })).concat([{ value: OTHER, label: OTHER, text: "How was identity confirmed?" }]);
const ACCEPTED = [
  { value: "accepted", label: "Accepted Service" },
  { value: "tried_refuse", label: "Tried to Refuse Service" },
];
const MANNER = [
  { value: "With direct delivery", label: "With direct delivery" },
  { value: OTHER, label: OTHER, text: "How were the papers delivered?" },
];
const GENDER = ["Male", "Female", "Unknown"].map((l) => ({ value: l, label: l }));
const RACE = ["White", "Black", "Asian", "Hispanic", "Middle Eastern", "American Indian", "Pacific Islander", "Unknown"]
  .map((l) => ({ value: l, label: l }));
const AGE = ["Under 15", "15-17", "18-25", "25-35", "35-45", "45-55", "55-65", "Over 65", "Unknown"]
  .map((l) => ({ value: l, label: l }));
const HEIGHT = ["Under 5'", "5'-5'4\"", "5'4\"-5'6\"", "5'6\"-5'8\"", "5'8\"-5'10\"", "5'10\"-6'0\"", "6'0\"-6'2\"", "6'2\"-6'6\"", "Over 6'6\"", "Unknown"]
  .map((l) => ({ value: l, label: l }));
const WEIGHT = ["Under 80 lbs", "80-120", "120-140", "140-160", "160-180", "180-200", "200-240", "240-300", "Over 300", "Unknown"]
  .map((l) => ({ value: l, label: l }));
const HAIR = ["Brown", "Black", "Blonde", "Red", "Gray", "Dyed", "Bald", "Unknown"].map((l) => ({ value: l, label: l }));
// Values are the SPECIAL_FEATURES codes the record stores.
const FEATURES = [
  { value: "accent", label: "Accent" },
  { value: "beard", label: "Beard" },
  { value: "birthmark", label: "Birthmark" },
  { value: "glasses", label: "Glasses" },
  { value: "goatee", label: "Goatee" },
  { value: "mustache", label: "Mustache" },
  { value: "piercings", label: "Piercings" },
  { value: "scars", label: "Scar" },
  { value: "tattoos", label: "Tattoo" },
];

// ── The steps ────────────────────────────────────────────────────────────────
// kind: "pick" (tap = next), "multi" (check several, then Done), "when",
// "fields" (type, then Next), "info" (App-supplied content, then Next),
// "declaration", "photos" (last: Save Entry).
const STEPS = {
  start: { kind: "start", title: "Log Attempt / Service" },
  when: { kind: "when", title: "Date and Time of Activity" },
  // attempted
  whoSpoke: { kind: "pick", title: "Who Did You Speak With at Address?", key: "wzWho", options: WHO_SPOKE },
  whatSaid: { kind: "pick", title: "What Did They Say?", key: "wzSaid", options: WHAT_SAID },
  observed: { kind: "multi", title: "What Did You Observe at Address?", key: "wzObserved", options: OBSERVED, none: "Nothing" },
  otherContact: { kind: "multi", title: "Who Else Did You Speak With?", key: "wzOtherContact", options: OTHER_CONTACT, none: "Nobody" },
  whatHappened: { kind: "pick", title: "What Happened at Address?", key: "wzHappened", options: WHAT_HAPPENED },
  noAccess: { kind: "pick", title: "No Access:", key: "wzNoAccess", options: NO_ACCESS },
  // served
  addrType: { kind: "pick", title: "Address Type:", key: "wzAddrType", options: ADDRESS_TYPES },
  deliveredTo: { kind: "pick", title: "Who Did You Deliver To?", key: "wzDelivered" },
  bizAuth: { kind: "pick", title: "Is This Person Authorized to Accept Service?", key: "wzBizAuth",
    options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
  bizName: { kind: "fields", title: "Who Accepted for the Business?", fields: [
    { key: "wzContactName", label: "Their name *", placeholder: "Full name as given", required: true },
    { key: "wzContactTitle", label: "Their position *", placeholder: "e.g. Office Manager, Registered Agent", required: true }] },
  contact: { kind: "fields", title: "Who Took the Papers?", fields: [
    { key: "wzContactName", label: "Name *", placeholder: "Full name, or John/Jane Doe", required: true },
    { key: "wzContactRelation", label: "Relationship to the person to serve *", placeholder: "e.g. Spouse, Co-occupant", required: true }] },
  identity: { kind: "pick", title: "Identity Was Confirmed By:", key: "wzIdentity", options: IDENTITY },
  accepted: { kind: "pick", title: "Was Service Accepted?", key: "wzAccepted", options: ACCEPTED },
  manner: { kind: "pick", title: "By Serving the Subject:", key: "wzManner", options: MANNER },
  gender: { kind: "pick", title: "Gender:", key: "gender", options: GENDER },
  race: { kind: "pick", title: "Race:", key: "ethnicity", options: RACE },
  age: { kind: "pick", title: "Age:", key: "age", options: AGE },
  height: { kind: "pick", title: "Height:", key: "height", options: HEIGHT },
  weight: { kind: "pick", title: "Weight:", key: "weight", options: WEIGHT },
  hair: { kind: "pick", title: "Hair:", key: "hair", options: HAIR },
  features: { kind: "multi", title: "Features:", key: "features", options: FEATURES },
  mailing: { kind: "info", title: "Mailing" },
  postingOrder: { kind: "fields", title: "Court Order for Posting", fields: [
    { key: "wzPostingOrder", label: "Court order date / description", placeholder: "e.g. Order dated 10/20/2026 (CCP 415.45)" }] },
  declaration: { kind: "declaration" },
  photos: { kind: "photos", title: "Photos" },
};

const DESCRIPTION = ["gender", "race", "age", "height", "weight", "hair", "features"];

function stepList(form) {
  const f = form || {};
  const list = ["start"];
  if (!f.wzPath) return list;
  // Date, time and address fill in automatically (shown, and changeable, on
  // the last screen) — no separate step.
  if (f.wzPath === "attempted") {
    list.push("whoSpoke");
    if (f.wzWho && f.wzWho !== "nobody") list.push("whatSaid");
    list.push("observed", "otherContact");
    if (f.wzWho === "nobody") {
      list.push("whatHappened");
      if (f.wzHappened === "no_access") list.push("noAccess");
    }
  } else {
    list.push("addrType", "deliveredTo");
    const d = String(f.wzDelivered || "");
    const business = f.wzAddrType === "Business";
    if (d === "subject") {
      if (business) list.push("bizAuth", "bizName");
      else list.push("identity", "accepted", "manner");
      list.push.apply(list, DESCRIPTION);
    } else if (d === "posted") {
      // Court-ordered posting: the order, then the mailing that goes with it.
      list.push("postingOrder", "mailing");
    } else if ((d === "contact" || d.indexOf("person:") === 0) && business) {
      // At a business the person who takes the papers must be authorized to
      // accept them: service on the business, no due diligence, no mailing.
      list.push("bizAuth", "bizName");
      list.push.apply(list, DESCRIPTION);
    } else if (d === "contact" || d.indexOf("person:") === 0) {
      list.push("contact");
      list.push.apply(list, DESCRIPTION);
      list.push("mailing");
    }
  }
  list.push("declaration", "photos");
  return list;
}

// The step to show: the saved position when it is still on the path, else start.
function currentStep(form) {
  const list = stepList(form);
  const at = form && form.wzAt;
  return list.indexOf(at) >= 0 ? at : "start";
}
function nextStep(form, from) {
  const list = stepList(form);
  const i = list.indexOf(from);
  return i >= 0 && i < list.length - 1 ? list[i + 1] : from;
}
function prevStep(form, from) {
  const list = stepList(form);
  const i = list.indexOf(from);
  return i > 0 ? list[i - 1] : null;
}

// ── Answers → the existing form fields ──────────────────────────────────────
const labelOf = (opts, v) => ((opts || []).find((o) => o.value === v) || {}).label || "";
const txt = (v) => String(v == null ? "" : v).trim();
const pickText = (v, other) => (v === OTHER || v === "other" ? txt(other) : txt(v));

function attemptOutcome(f) {
  if (f.wzWho === "nobody") {
    switch (f.wzHappened) {
      case "no_answer": return "no_answer";
      case "no_access": case "hostile": return "inaccessible";
      case "vacant": return "vacant";
      case "no_such_address": return "wrong_address";
      case "need_unit": case "pmb": return "unable_to_locate";
      case "other": return "other";
      default: return "no_answer";
    }
  }
  const s = f.wzSaid;
  if (s === "Subject resides but refused to respond") return "refused";
  if (["Subject unknown", "Subject moved", "Subject no longer employed", "Subject known but never resided"].includes(s)) return "wrong_address";
  if (["Subject deceased", "Subject incarcerated", "Subject resides in a care facility"].includes(s)) return "unable_to_locate";
  if (s === "Service not permitted") return "inaccessible";
  if (s === OTHER) return "other";
  return "no_answer";
}

// Who answered, in the codes the record already uses.
function personFields(f) {
  const w = f.wzWho;
  if (!w) return {};
  if (w === "nobody") return { personPresent: "nobody", personPresentFamily: "", personPresentDetail: "" };
  if (w === "spouse") return { personPresent: "spouse", personPresentFamily: "", personPresentDetail: "" };
  if (w === "roommate") return { personPresent: "roommate", personPresentFamily: "", personPresentDetail: "Roommate" };
  if (w === "resident") return { personPresent: "roommate", personPresentFamily: "", personPresentDetail: "Resident" };
  if (w === "parent") return { personPresent: "family_member", personPresentFamily: "parent", personPresentDetail: "Parent" };
  if (w === "sibling") return { personPresent: "family_member", personPresentFamily: "sibling", personPresentDetail: "Sibling" };
  if (w === "son" || w === "daughter") return { personPresent: "family_member", personPresentFamily: "adult_child", personPresentDetail: w === "son" ? "Son" : "Daughter" };
  return { personPresent: "other", personPresentFamily: "", personPresentDetail: txt(f.wzWhoOther) };
}

function observedText(f) {
  const list = (Array.isArray(f.wzObserved) ? f.wzObserved : []).filter((v) => v && v !== "Nothing");
  const parts = [];
  if (f.wzWho === "nobody" && f.wzHappened) {
    const h = f.wzHappened === "other" ? txt(f.wzHappenedOther) : labelOf(WHAT_HAPPENED, f.wzHappened);
    const why = f.wzHappened === "no_access" ? pickText(f.wzNoAccess, f.wzNoAccessOther) : "";
    if (h) parts.push(why ? `${h} — ${why}` : h);
  }
  parts.push.apply(parts, list);
  if (!parts.length && (Array.isArray(f.wzObserved) && f.wzObserved.includes("Nothing"))) return "Nothing notable";
  return parts.join("; ");
}

function derive(form) {
  const f = form || {};
  if (!f.wzPath) return {};
  const out = {};
  if (f.wzPath === "attempted") {
    Object.assign(out, personFields(f));
    out.status = "attempted";
    out.serviceType = "";
    out.outcome = attemptOutcome(f);
    out.personSaid = f.wzWho && f.wzWho !== "nobody" ? pickText(f.wzSaid, f.wzSaidOther) : "";
    out.observed = observedText(f);
    out.observedOther = "";
    const oc = (Array.isArray(f.wzOtherContact) ? f.wzOtherContact : []).filter((v) => v && v !== "Nobody");
    out.otherContact = oc.length ? oc.join("; ") : (Array.isArray(f.wzOtherContact) && f.wzOtherContact.length ? "Nobody else" : "");
    out.identification = ""; out.serviceAccepted = ""; out.relationship = "";
    return out;
  }
  // served
  out.locationType = txt(f.wzAddrType);
  out.personSaid = ""; out.observed = ""; out.observedOther = ""; out.otherContact = "";
  const d = String(f.wzDelivered || "");
  if (d === "posted") {
    // Court-ordered posting: a completed service, proved on POS-010 item 5d.
    return Object.assign(out, { status: "served", outcome: "served_posting", serviceType: "posting",
      personPresent: "", personPresentDetail: "", relationship: "", identification: "", serviceAccepted: "" });
  }
  if (d === "subject" || ((d === "contact" || d.indexOf("person:") === 0) && f.wzAddrType === "Business")) {
    out.status = "served"; out.outcome = "served_personal"; out.serviceType = "personal";
    if (f.wzAddrType === "Business") {
      // Same fields the business-authority answer always set.
      out.personPresent = "other";
      out.personPresentDetail = txt(f.wzContactName);
      out.relationship = txt(f.wzContactTitle);
      out.serviceAccepted = f.wzBizAuth === "yes" ? "Authorized to accept service" : "";
      out.identification = "";
    } else {
      out.personPresent = "subject"; out.personPresentDetail = ""; out.personPresentFamily = ""; out.relationship = "";
      out.identification = pickText(f.wzIdentity, f.wzIdentityOther);
      out.serviceAccepted = f.wzAccepted === "tried_refuse" ? "Refused service — documents left at feet or nearby"
        : f.wzAccepted === "accepted" ? "Accepted service" : "";
    }
    return out;
  }
  if (d === "contact" || d.indexOf("person:") === 0) {
    out.status = "served"; out.outcome = "served_substitute"; out.serviceType = "substitute";
    out.personPresent = "other"; out.personPresentFamily = "";
    out.personPresentDetail = txt(f.wzContactName);
    out.relationship = txt(f.wzContactRelation);
    out.identification = ""; out.serviceAccepted = "Accepted service";
    return out;
  }
  return out;
}

// Apply one answer and re-derive everything that depends on it. The declaration
// text is rebuilt too, unless it was edited by hand.
function applyAnswer(form, patch) {
  const merged = Object.assign({}, form || {}, patch || {});
  const next = Object.assign(merged, derive(merged));
  if (!next.wzDeclEdited) next.desc = buildDeclaration(next);
  return next;
}

// ── The declaration, written from the answers ───────────────────────────────
function lowerFirst(s) { const t = txt(s); return t ? t.charAt(0).toLowerCase() + t.slice(1) : ""; }
function descriptionText(f) {
  const bits = [];
  if (f.hair && f.hair !== "Unknown") bits.push(`${lowerFirst(f.hair)}-haired`);
  if (f.ethnicity && f.ethnicity !== "Unknown") bits.push(f.ethnicity);
  if (f.gender && f.gender !== "Unknown") bits.push(lowerFirst(f.gender));
  let s = bits.length ? `${/^[aeiouAEIOU]/.test(bits[0]) ? "an" : "a"} ${bits.join(" ")}` : "an individual";
  const more = [];
  if (f.age && f.age !== "Unknown") more.push(`${f.age} years of age`);
  if (f.height && f.height !== "Unknown") more.push(`${f.height} tall`);
  if (f.weight && f.weight !== "Unknown") more.push(`weighing ${f.weight}${/lbs/.test(f.weight) ? "" : " lbs"}`);
  if (more.length) s += `, ${more.length > 1 ? more.slice(0, -1).join(", ") + " and " + more[more.length - 1] : more[0]}`;
  const feats = (Array.isArray(f.features) ? f.features : []).map((v) => labelOf(FEATURES, v)).filter(Boolean);
  if (feats.length) s += `, with ${feats.join(", ").toLowerCase()}`;
  return s;
}
function buildDeclaration(form) {
  const f = form || {};
  const subject = txt(f.wzSubject) || "the person to be served";
  if (f.wzPath === "attempted") {
    if (f.wzWho === "nobody") {
      let s;
      if (f.wzHappened === "no_access") {
        const why = pickText(f.wzNoAccess, f.wzNoAccessOther);
        s = `I was unable to access the address${why ? ` (${lowerFirst(why)})` : ""}.`;
      } else if (f.wzHappened === "no_answer" || !f.wzHappened) s = "No one answered the door.";
      else if (f.wzHappened === "vacant") s = "The property appeared vacant.";
      else if (f.wzHappened === "need_unit") s = "A unit number is needed to locate the subject.";
      else if (f.wzHappened === "no_such_address") s = "The address does not exist.";
      else if (f.wzHappened === "pmb") s = "The address is a PMB/PO Box.";
      else if (f.wzHappened === "hostile") s = "I left due to a hostile environment.";
      else s = `${txt(f.wzHappenedOther) || "Other"}.`.replace(/\.\.$/, ".");
      return [s].concat(extraSentences(f)).join(" ");
    }
    const who = f.wzWho === "other" ? (txt(f.wzWhoOther) || "another person") : `the ${lowerFirst(labelOf(WHO_SPOKE, f.wzWho))}`;
    const said = pickText(f.wzSaid, f.wzSaidOther);
    const s = `I spoke with an individual who identified themselves as ${who}${said ? ` and they stated ${lowerFirst(said)}${/available$/.test(said) ? " at this time" : ""}` : ""}.`;
    return [s].concat(extraSentences(f)).join(" ");
  }
  if (f.wzPath === "served") {
    const d = String(f.wzDelivered || "");
    if (d === "posted") return `I posted the documents in a conspicuous place at the address for ${subject}${txt(f.wzPostingOrder) ? `, as authorized by court order (${txt(f.wzPostingOrder)})` : ", as authorized by court order"}.`;
    if (d === "subject" || ((d === "contact" || d.indexOf("person:") === 0) && f.wzAddrType === "Business")) {
      if (f.wzAddrType === "Business") {
        const n = txt(f.wzContactName) || "___"; const t = txt(f.wzContactTitle);
        return `I delivered the documents to ${n}${t ? `, ${t},` : ""} who is authorized to accept service for ${subject}. The individual appeared to be ${descriptionText(f)}.`;
      }
      const idt = pickText(f.wzIdentity, f.wzIdentityOther);
      const how = f.wzManner === OTHER ? lowerFirst(f.wzMannerOther) : "with direct delivery";
      const acc = f.wzAccepted === "tried_refuse" ? "tried to refuse service; the documents were left in their presence" : `accepted service${how ? ` ${how}` : ""}`;
      return `I delivered the documents to ${subject}${idt ? ` with identity confirmed by ${lowerFirst(idt)}` : ""}. The individual ${acc}. The individual appeared to be ${descriptionText(f)}.`;
    }
    if (d === "contact" || d.indexOf("person:") === 0) {
      const n = txt(f.wzContactName) || "___"; const rel = txt(f.wzContactRelation);
      return `I delivered the documents for ${subject} to ${n}${rel ? `, ${lowerFirst(rel)} of the subject,` : ""} a competent member of the household/person apparently in charge. The individual appeared to be ${descriptionText(f)}.`;
    }
  }
  return "";
}
function extraSentences(f) {
  const out = [];
  const obs = (Array.isArray(f.wzObserved) ? f.wzObserved : []).filter((v) => v && v !== "Nothing");
  if (obs.length) out.push(`I observed: ${obs.map(lowerFirst).join("; ")}.`);
  const oc = (Array.isArray(f.wzOtherContact) ? f.wzOtherContact : []).filter((v) => v && v !== "Nobody");
  if (oc.length) out.push(`I also spoke with a ${oc.map(lowerFirst).join("; a ")}.`);
  return out;
}

// ── "Vary your attempts" tracker ─────────────────────────────────────────────
function varyAttempts(attempts) {
  const list = (Array.isArray(attempts) ? attempts : []).filter(Boolean);
  const hour = (t) => { const m = /^(\d{1,2}):/.exec(String(t || "")); return m ? Number(m[1]) : null; };
  const dow = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || "")); return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay() : null; };
  const days = new Set(list.map((a) => String(a.attempt_date || "").slice(0, 10)).filter(Boolean));
  return {
    morning: list.some((a) => { const h = hour(a.attempt_time); return h != null && h < 12; }),
    evening: list.some((a) => { const h = hour(a.attempt_time); return h != null && h >= 17; }),
    weekends: list.some((a) => { const d = dow(a.attempt_date); return d === 0 || d === 6; }),
    newDays: days.size >= 3,
  };
}

module.exports = {
  OTHER, WHO_SPOKE, WHAT_SAID, OBSERVED, OTHER_CONTACT, WHAT_HAPPENED, NO_ACCESS, ADDRESS_TYPES,
  IDENTITY, ACCEPTED, MANNER, GENDER, RACE, AGE, HEIGHT, WEIGHT, HAIR, FEATURES,
  STEPS, stepList, currentStep, nextStep, prevStep, derive, applyAnswer, buildDeclaration, varyAttempts, attemptOutcome,
};
